/**
 * Team Management Hooks
 *
 * React hooks for team API integration
 */

import { useState, useCallback, useEffect } from 'react';
import { getAccessToken } from '@lib/msal';

// Team types (matching shared package)
export type TeamRole = 'Admin' | 'Regular' | 'ReadOnly';
export type TeamStatus = 'Active' | 'Deactivated';

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: TeamRole;
  status: TeamStatus;
  invitedBy: string;
  invitedAt: string;
  lastLoginAt?: string;
  deactivatedAt?: string;
  deactivatedBy?: string;
}

// Normalize API base - remove trailing /api if present to avoid double /api/api
const rawApiBase = import.meta.env.VITE_API_URL || '';
const API_BASE = rawApiBase.replace(/\/api\/?$/, '');

interface TeamMembershipResponse {
  isMember: boolean;
  reason?: 'not_in_team' | 'deactivated';
  member?: TeamMember;
}

interface TeamListResponse {
  members: TeamMember[];
  total: number;
}

/**
 * Hook to check current user's team membership
 */
export function useTeamMembership() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const checkMembership = useCallback(async (): Promise<TeamMembershipResponse | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError(new Error('Not authenticated'));
        return null;
      }

      const response = await fetch(`${API_BASE}/api/team/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to check team membership');
      }

      const data = await response.json();
      return data as TeamMembershipResponse;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { checkMembership, isLoading, error };
}

/**
 * Hook to list all team members (Admin only)
 * Auto-fetches on mount
 */
export function useTeamList() {
  const [data, setData] = useState<TeamMember[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/team`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have permission to view team members');
        }
        throw new Error('Failed to fetch team members');
      }

      const result: TeamListResponse = await response.json();
      setData(result.members);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, error, refetch };
}

/**
 * Hook for team member operations (invite, update, remove)
 */
export function useTeamOperations() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const invite = useCallback(
    async (params: { email: string; name: string; role: TeamRole }): Promise<TeamMember> => {
      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE}/api/team`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to invite member');
        }

        return data.member as TeamMember;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const updateRole = useCallback(async (email: string, role: TeamRole): Promise<TeamMember> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/team/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update role');
      }

      return data.member as TeamMember;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateStatus = useCallback(async (email: string, status: TeamStatus): Promise<TeamMember> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/team/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update status');
      }

      return data.member as TeamMember;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const remove = useCallback(async (email: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/team/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to remove member');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const bootstrap = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/team/bootstrap`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Handle empty or non-JSON responses
      const text = await response.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        // If response isn't JSON, create error from status
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        data = {};
      }

      if (!response.ok) {
        throw new Error(data.error || `Failed to bootstrap (${response.status})`);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    invite,
    updateRole,
    updateStatus,
    remove,
    bootstrap,
    isLoading,
    error,
    clearError: () => setError(null),
  };
}
